/**
 * What fills the screen in place of the stage (spec 08 §1, 02 §9.4): the title and the story (the
 * TITLE stream's scenes) and the ending. One at a time; each is made when it is shown and freed when it
 * goes, and made again in the new colours when the look changes while it is up (the options are set
 * from the title's menu), carrying on from where it had got to.
 */

import type { Object3D, Scene, Texture } from 'three';
import type { Palette } from '../palette';
import { StoryView, TitleView } from '../title';
import { EndingView } from './endingView';
import type { FigureSet } from './figureSet';

export type BackdropKind = 'title' | 'story' | 'ending';

export class Backdrops {
  private ending: EndingView | null = null;
  private title: TitleView | StoryView | null = null;
  private titleStart = -1;
  /** What is up, or null for the stage. */
  showing: BackdropKind | null = null;

  constructor(private readonly scene: Scene, private readonly gradientMap: Texture) {}

  /** Show `kind` (null: none). `figures` lends the ending its materials; `palette` colours the title. */
  show(kind: BackdropKind | null, figures: FigureSet, palette: Palette): void {
    if (kind !== 'ending' && this.ending) {
      this.scene.remove(this.ending.group);
      this.ending.dispose();
      this.ending = null;
    }
    if (this.title && !(kind === 'title' && this.title instanceof TitleView)
      && !(kind === 'story' && this.title instanceof StoryView)) {
      this.scene.remove(this.title.group);
      this.title.dispose();
      this.title = null;
    }
    if (kind === 'ending' && !this.ending) {
      this.ending = new EndingView(figures.palette, figures.glow, figures.bossMaterial);
      this.scene.add(this.ending.group);
    } else if ((kind === 'title' || kind === 'story') && !this.title) {
      const kit = { palette, gradientMap: this.gradientMap };
      this.title = kind === 'title' ? new TitleView(kit) : new StoryView(kit);
      this.titleStart = -1;
      this.scene.add(this.title.group);
    }
    this.showing = kind;
  }

  /** The look changed: the backdrop that is up is made again in its colours, at the same point. */
  rebuild(figures: FigureSet, palette: Palette): void {
    const kind = this.showing;
    const start = this.titleStart;
    this.show(null, figures, palette);
    this.show(kind, figures, palette);
    this.titleStart = start;
  }

  /** Animate whatever is up; `t` in seconds. */
  frame(t: number): void {
    this.ending?.update(t);
    if (!this.title) return;
    if (this.titleStart < 0) this.titleStart = t;
    this.title.update(t - this.titleStart);
  }

  /** Whether `o` is one of the backdrops' own groups (never hidden with the figures). */
  owns(o: Object3D): boolean {
    return o === this.ending?.group || o === this.title?.group;
  }
}
